import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { actionClearFAIStatus } from 'lib/actions';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAICurrentInspectionElapsedSince } from 'lib/selectors/getFAICurrentInspectionElapsedSince';
import { getFAIApproved } from 'lib/selectors/getFAIApproved';
import { getFAIRejected } from 'lib/selectors/getFAIRejected';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getAdjustedSetupDurationDates } from 'lib/selectors/getAdjustedSetupDurationDates';
import { getLatestSetupActivity } from 'lib/selectors/getLatestSetupActivity';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { formatTime } from 'lib/utils/date';
import { formatFAIEvaluatorDisplayName } from 'lib/utils/faiEvaluatorDisplay';
import { formatExpectedSetupDurationForDisplay } from 'lib/utils/setupDurationDisplay';
import { Title } from 'pages/Machine/MachineMainWrapper/styled';
import useStartSubmitWork from 'lib/hooks/useStartSubmitWork';
import {
  FullViewportOverlay,
  FaiMainContainer,
  FaiBannerBody,
  DetailsBlock,
  MetaLine,
  EvaluationNote,
  FooterBar,
  FooterButtonPrimary,
} from './styled';
import { FAISetupElapsedRow } from './FAISetupElapsedRow';
import { FAIWaitingTimer } from './FAIWaitingTimer';

const getEvaluatorDisplay = (inspection) => {
  return {
    evaluatorName: formatFAIEvaluatorDisplayName(inspection),
    evaluatedTimeStr: inspection.evaluatedAt
      ? formatTime(inspection.evaluatedAt)
      : '',
  };
};

const FAIEvaluatedBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inspection = useSelector(getFAICurrentInspection);
  const isApproved = useSelector(getFAIApproved);
  const isRejected = useSelector(getFAIRejected);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const { adjustedSetupStartDate } = useSelector(getAdjustedSetupDurationDates);
  const latestSetupActivity = useSelector(getLatestSetupActivity);
  const isPaused = useSelector(getIsPaused);
  const elapsedSince = useSelector(getFAICurrentInspectionElapsedSince);
  const startSubmitWork = useStartSubmitWork();

  if (!inspection || (!isApproved && !isRejected)) {
    return null;
  }

  const { evaluatorName, evaluatedTimeStr } = getEvaluatorDisplay(inspection);
  const titleKey = isRejected ? 'fai.rejected' : 'fai.approved';
  const metaKey = isRejected ? 'fai.rejectedAtBy' : 'fai.approvedAtBy';
  const reason = isRejected ? inspection.rejectionReason : null;
  const comment = isRejected ? inspection.evaluationNote : null;

  const setupExpectedDisplay = formatExpectedSetupDurationForDisplay(
    latestActivitySet?.expectedSetupDuration
  );
  const showSetupLine = Boolean(
    latestActivitySet?.activitySetRef ||
      latestActivitySet?.workOrderOperationRef ||
      latestActivitySet?.expectedSetupDuration ||
      adjustedSetupStartDate
  );

  const setupElapsedProps = {
    showSetupLine,
    adjustedSetupStartDate,
    isPaused,
    latestSetupActivity,
    setupExpectedDisplay,
  };

  return (
    <FullViewportOverlay data-testid="fai-banner">
      <FaiMainContainer
        className={`fai-banner-${isRejected ? 'rejected' : 'approved'}`}
      >
        <FaiBannerBody>
          <Title>{t(titleKey)}</Title>
          <DetailsBlock>
            {evaluatedTimeStr && evaluatorName ? (
              <MetaLine>
                {t(metaKey, { time: evaluatedTimeStr, name: evaluatorName })}
              </MetaLine>
            ) : null}
            {reason ? (
              <MetaLine>
                {t('fai.rejectedReason', { reason })}
                {comment ? ` | ${comment}` : ''}
              </MetaLine>
            ) : comment ? (
              <EvaluationNote>{comment}</EvaluationNote>
            ) : null}
            <FAISetupElapsedRow {...setupElapsedProps} />
          </DetailsBlock>
          <FAIWaitingTimer startDate={elapsedSince} />
        </FaiBannerBody>
        <FooterBar>
          {!isRejected ? (
            <FooterButtonPrimary
              onClick={() => {
                startSubmitWork();
              }}
            >
              {t('fai.enterProduction')}
            </FooterButtonPrimary>
          ) : (
            <FooterButtonPrimary
              onClick={() => {
                dispatch(actionClearFAIStatus());
              }}
            >
              {t('fai.dismiss')}
            </FooterButtonPrimary>
          )}
        </FooterBar>
      </FaiMainContainer>
    </FullViewportOverlay>
  );
};

export { FAIEvaluatedBanner };
